import { Box } from '@cad-arc';
import { EventQueueRowData, UnitQueueRowData } from '../../../socket/stream-types';
import { observer } from 'mobx-react-lite';
import { BaseQueueCell } from '../base-queue-cell';
import { getEventQueueRowData } from '../../utils/get-event-queue-row-data';
import { getPriorityFlashBackgroundImage } from '../../../configurable-colors-sounds/priority-flash-image';
import { usePriorityColor } from '../../../configurable-colors-sounds/use-configured-colors';
import {
    getPriorityFlashColor,
    getPrioritySolidFill,
    isUnassignedTimerFlashing,
} from '../../../configurable-colors-sounds/priority-colors';

export const PriorityCell = observer(function PriorityCell({
    data,
}: {
    data: EventQueueRowData | UnitQueueRowData;
}): React.ReactElement {
    const dataToUse = getEventQueueRowData(data);
    const configuredColor = usePriorityColor(
        dataToUse?.priorityId ?? null,
        dataToUse?.agencyTypeGlobalAttrId
    );
    if (!dataToUse) {
        return <span />;
    }
    const isFlashing = isUnassignedTimerFlashing(dataToUse);
    const solidFill = isFlashing
        ? null
        : getPrioritySolidFill(configuredColor, dataToUse.isPriority1);

    return (
        // Inset and rounded like the color-coded unit status cells. The flash is painted on the cell
        // itself so it gets the same shape as the solid fill.
        <BaseQueueCell
            alignment="CENTER"
            data-testid="PRIORITY"
            backgroundColor={solidFill?.background}
            backgroundImage={
                isFlashing
                    ? getPriorityFlashBackgroundImage(getPriorityFlashColor(configuredColor))
                    : undefined
            }
            // The fill's text color only applies while no update notification overrides it.
            idleColor={solidFill?.text}
            border="md"
            borderColor="transparent"
            backgroundClip="padding-box"
            borderRadius="md"
        >
            {/* Bold on an inner span so it doesn't override the cell's own bold for update notifications */}
            {configuredColor.matched ? (
                <Box as="span" fontWeight="bold">
                    {dataToUse.priority}
                </Box>
            ) : (
                dataToUse.priority
            )}
        </BaseQueueCell>
    );
});
